import type { SbAuthIdentifier } from "@/types";

import { SbPasswordField } from "./_password-field";
import type {
  BaseFieldValidation,
  ConfirmFieldValidation,
  ConfirmFieldVariant,
  SbConfirmFieldProps as ConfirmProps,
  EmailFieldValidation,
  SbEmailFieldProps as EmailProps,
  IdentifierFieldValidation,
  SbIdentifierFieldProps as IdentifierProps,
  NameFieldValidation,
  SbNameFieldProps as NameProps,
  PasswordFieldRuleName,
  PasswordFieldValidation,
  PasswordFieldVariant,
  SbPasswordFieldProps as PasswordProps,
  PhoneFieldValidation,
  SbPhoneFieldProps as PhoneProps,
  UsernameFieldValidation,
  SbUsernameFieldProps as UsernameProps,
} from "./field.types";

export const genRequiredError = (label: string) =>
  `${label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()} is a required field.`;

export const genFormatError = (label: string) =>
  `Please enter a valid ${label.toLowerCase()}.`;

export const genMinlengthError = (label: string, minlength?: number) =>
  minlength
    ? `${label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()} must be at least ${minlength} characters.`
    : `Please enter a longer ${label.toLowerCase}`;

export const genMaxlengthError = (label: string, maxlength?: number) =>
  maxlength
    ? `${label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()} can have at most ${maxlength} characters.`
    : `Please enter a shorter ${label.toLowerCase}`;

/* ------------------------------------------------------------ */

export const EMAIL_FIELD_DEFAULT_PROPS: Omit<EmailProps, "seed" | "inputId"> = {
  name: "email",
  label: "Email",
  pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.source,
  validation: "default",
  requiredError: genRequiredError("Email"),
  formatError: genFormatError("Email"),
};

export const PHONE_FIELD_DEFAULT_PROPS: Omit<PhoneProps, "seed" | "inputId"> = {
  name: "phone",
  label: "Phone",
  pattern: /^\+?(\d[\d-.() ]+)?(\([\d-.() ]+\))?[\d-.() ]+\d$/.source,
  validation: "default",
  requiredError: genRequiredError("Phone"),
  formatError: genFormatError("Phone"),
};

export const USERNAME_FIELD_DEFAULT_PROPS: Omit<
  UsernameProps,
  "seed" | "inputId"
> = {
  name: "username",
  label: "Username",
  pattern: /^[a-z0-9_]*$/.source,
  validation: "default",
  requiredError: genRequiredError("Username"),
  minlengthError: genMinlengthError("Username"),
  maxlengthError: genMaxlengthError("Username"),
  formatError: genFormatError("Username"),
};

export const NAME_FIELD_DEFAULT_PROPS: Omit<NameProps, "seed" | "inputId"> = {
  name: "name",
  label: "Name",
  pattern: /^[\p{L} .'-]*$/u.source,
  validation: "default",
  requiredError: genRequiredError("Name"),
  minlengthError: genMinlengthError("Name"),
  maxlengthError: genMaxlengthError("Name"),
  formatError: genFormatError("Name"),
};

export const IDENTIFIER_FIELD_DEFAULT_PROPS: Omit<
  IdentifierProps,
  "seed" | "inputId"
> = {
  name: "identifier",
  identifiers: [],
  label: "Identifier",
  validation: "default",
  requiredError: genRequiredError("Identifier"),
};

export const PASSWORD_FIELD_DEFAULT_PROPS: Omit<
  PasswordProps,
  "seed" | "inputId"
> = {
  name: "password",
  label: "Password",
  variant: "toggle",
  rules: [
    { name: "uppercase", error: "Must contain an uppercase letter." },
    { name: "lowercase", error: "Must contain a lowercase letter." },
    { name: "number", error: "Must contain a number." },
    {
      name: "special",
      error:
        "Must contain a special character.                                                                                                                                                                                                                                             ",
    },
  ],
  validation: "default",
  requiredError: genRequiredError("Password"),
  minlengthError: genMinlengthError("Password"),
  maxlengthError: genMaxlengthError("Password"),
  formatError: genFormatError("Password"),
  forgotLabel: "Forgot your password?",
};

export const CONFIRM_FIELD_DEFAULT_PROPS: Omit<
  ConfirmProps,
  "seed" | "inputId"
> = {
  name: "confirm",
  label: "Confirm Password",
  variant: "toggle",
  pair: SbPasswordField.TAG,
  validation: "default",
  requiredError: genRequiredError("Confirm Password"),
  matchError: "Passwords do not match.",
};

/* ------------------------------------------------------------ */

const VALIDATIONS_REC: Record<BaseFieldValidation, BaseFieldValidation> = {
  default: "default",
  simple: "simple",
};

export const EMAIL_FIELD_VALIDATIONS: EmailFieldValidation[] =
  Object.values(VALIDATIONS_REC);
export const PHONE_FIELD_VALIDATIONS: PhoneFieldValidation[] =
  Object.values(VALIDATIONS_REC);
export const USERNAME_FIELD_VALIDATIONS: UsernameFieldValidation[] =
  Object.values(VALIDATIONS_REC);
export const NAME_FIELD_VALIDATIONS: NameFieldValidation[] =
  Object.values(VALIDATIONS_REC);
export const IDENTIFIER_FIELD_VALIDATIONS: IdentifierFieldValidation[] =
  Object.values(VALIDATIONS_REC);
export const PASSWORD_FIELD_VALIDATIONS: PasswordFieldValidation[] =
  Object.values(VALIDATIONS_REC);
export const CONFIRM_FIELD_VALIDATIONS: ConfirmFieldValidation[] =
  Object.values(VALIDATIONS_REC);

/* ---------------------------------------- */

const AUTH_IDENTIFIERS_REC: Record<SbAuthIdentifier, SbAuthIdentifier> = {
  email: "email",
  phone: "phone",
  username: "username",
};

export const AUTH_IDENTIFIERS: SbAuthIdentifier[] =
  Object.values(AUTH_IDENTIFIERS_REC);

/* ---------------------------------------- */

const PASSWORD_FIELD_VARIANTS_REC: Record<
  PasswordFieldVariant,
  PasswordFieldVariant
> = {
  toggle: "toggle",
  simple: "simple",
};

export const PASSWORD_FIELD_VARIANTS: PasswordFieldVariant[] = Object.values(
  PASSWORD_FIELD_VARIANTS_REC,
);
export const CONFIRM_FIELD_VARIANTS: ConfirmFieldVariant[] = Object.values(
  PASSWORD_FIELD_VARIANTS_REC,
);

/* ---------------------------------------- */

const PASSWORD_FIELD_RULE_NAMES_REC: Record<
  PasswordFieldRuleName,
  PasswordFieldRuleName
> = {
  uppercase: "uppercase",
  lowercase: "lowercase",
  number: "number",
  special: "special",
};

export const PASSWORD_FIELD_RULE_NAMES: PasswordFieldRuleName[] = Object.values(
  PASSWORD_FIELD_RULE_NAMES_REC,
);
